import React, { useState } from "react";
import moment from "moment";
import { Helmet } from "react-helmet";
import { Container } from "reactstrap";

import { Heading } from "../../components/Heading";
import { PageHeadings } from "../../constants/PageHeading";
import { PageTitles } from "../../constants";
import { GetAnalytics, GetVisuals } from "../../api/api.service";
import { getKey, MODULE_KEYS } from "../../api/api.keys";
import { useRequestProcessor } from "../../hooks/useRequestProcessors";

import AnalyticsSection from "./SummarySection";
import GraphSection from "./GraphSection";
import { CustomButton } from "../../components/CustomButton";
import { DateRangePicker } from "../../components/DateRangePicker";

const Dashboard = () => {
    const { useFetch } = useRequestProcessor();
    const [activeButton, setActiveButton] = useState("thisWeek");
    const [dateRange, setDateRange] = useState({
        startDate: moment().subtract(7, "days").format("YYYY-MM-DD"),
        endDate: moment().format("YYYY-MM-DD")
    });

    const { data: analytics, isFetching: isAnalyticsFetching } = useFetch(
        getKey([MODULE_KEYS.ANALYTICS, dateRange.startDate, dateRange.endDate]),
        async ({ signal }) =>
            await GetAnalytics({
                params: {
                    ...(dateRange.startDate && { startDate: dateRange.startDate }),
                    ...(dateRange.endDate && { endDate: dateRange.endDate })
                },
                signal
            })
    );

    const { data: visuals, isFetching: isVisualsFetching } = useFetch(
        getKey([MODULE_KEYS.VISUALS, dateRange.startDate, dateRange.endDate]),
        async ({ signal }) =>
            await GetVisuals({
                params: {
                    ...(dateRange.startDate && { startDate: dateRange.startDate }),
                    ...(dateRange.endDate && { endDate: dateRange.endDate })
                },
                signal
            })
    );

    const getDateRange = (filter) => {
        switch (filter) {
            case "today":
                return {
                    startDate: moment().format("YYYY-MM-DD"),
                    endDate: moment().format("YYYY-MM-DD")
                };
            case "thisWeek":
                return {
                    startDate: moment().subtract(7, "days").format("YYYY-MM-DD"),
                    endDate: moment().format("YYYY-MM-DD")
                };
            case "thisMonth":
                return {
                    startDate: moment().subtract(30, "days").format("YYYY-MM-DD"),
                    endDate: moment().format("YYYY-MM-DD")
                };
            default:
                return {
                    startDate: "",
                    endDate: ""
                };
        }
    };

    const handleClick = (buttonId) => {
        setActiveButton(buttonId);
        setDateRange(getDateRange(buttonId));
    };

    const handleDateChange = (dates) => {
        if (dates[0] && dates[1]) {
            setDateRange({
                startDate: moment(dates[0]).format("YYYY-MM-DD"),
                endDate: moment(dates[1]).format("YYYY-MM-DD")
            });
        } else {
            // Reset to the currently active button's date range
            setDateRange(getDateRange(activeButton));
        }
    };

    return (
        <div className="page-content">
            <Helmet>
                <title>{PageTitles.DASHBOARD} </title>
            </Helmet>
            <Container fluid>
                <Heading heading={PageHeadings.DASHBOARD} />

                <div className="clearfix filters-btn d-flex justify-content-end mb-4">
                    <div className="d-flex align-items-center justify-content-end me-2">
                        Filter by
                    </div>
                    <div className="d-flex justify-content-end bg-white p-1 rounded">
                        <CustomButton
                            title="Today"
                            className={`chart-button shadow-none me-1 ${
                                activeButton === "today" ? "active" : ""
                            }`}
                            color="#704DE7"
                            outline
                            onClick={() => handleClick("today")}
                        />
                        <CustomButton
                            title="This Week"
                            className={`chart-button shadow-none me-1 ${
                                activeButton === "thisWeek" ? "active" : ""
                            }`}
                            color="#704DE7"
                            outline
                            onClick={() => handleClick("thisWeek")}
                        />
                        <CustomButton
                            className={`chart-button shadow-none me-1 ${
                                activeButton === "thisMonth" ? "active" : ""
                            }`}
                            color="#704DE7"
                            outline
                            onClick={() => handleClick("thisMonth")}
                            title="This Month"
                        />
                        <div className="d-flex align-items-center justify-content-end date-picker">
                            <DateRangePicker onDateChange={handleDateChange} />
                        </div>
                    </div>
                </div>

                <AnalyticsSection analytics={analytics} isAnalyticsFetching={isAnalyticsFetching} />
                <GraphSection visuals={visuals} isVisualsFetching={isVisualsFetching} />
            </Container>
        </div>
    );
};

export default Dashboard;
